import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../utils/helpers';
import { Badge } from '../components/ui/badge';
import { CourseCard, ServiceCard } from '../components/Cards';
import { ChevronRight, MapPin, BookOpen, Globe, Heart, GraduationCap } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [school, setSchool] = useState(null);
  const [courses, setCourses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const schoolRes = await axios.get(`${API_URL}/api/schools/${id}`);
        const schoolData = schoolRes.data;
        setSchool(schoolData);
        
        // Load courses
        const coursesRes = await axios.get(`${API_URL}/api/courses`, { params: { school_id: id } });
        setCourses(coursesRes.data);
        
        // If school user also has a teacher profile, load services
        if (schoolData.has_teacher_profile && schoolData.teacher_id) {
          const servicesRes = await axios.get(`${API_URL}/api/services`, { params: { teacher_id: schoolData.teacher_id } });
          setServices(servicesRes.data);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <div className="text-center py-12">{t('common.loading')}</div>;
  if (!school) return <div className="text-center py-12">Escuela no encontrada</div>;

  const hasImage = school.images && school.images.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="school-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden p-8">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-24 h-24 rounded-full bg-cream-dark flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: '#F5EFE6' }}>
            {hasImage ? (
              <img src={school.images[0]} alt={getLocalizedText(school.name, i18n.language)} className="w-full h-full object-cover" />
            ) : (
              <BookOpen className="h-12 w-12 text-gold" style={{ color: '#D4A574' }} />
            )}
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary">{getLocalizedText(school.name, i18n.language)}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-blue-500 text-white">Escuela</Badge>
              {school.has_teacher_profile && <Badge className="bg-purple-500 text-white">Profesional</Badge>}
            </div>
            <div className="flex items-center text-secondary mt-2">
              <MapPin className="h-4 w-4 mr-1" />
              {school.location?.city}, {school.location?.country}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
            <GraduationCap className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
            <p className="text-sm text-secondary">Cursos</p>
            <p className="font-semibold">{courses.length}</p>
          </div>
          {services.length > 0 && (
            <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
              <Heart className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
              <p className="text-sm text-secondary">Servicios</p>
              <p className="font-semibold">{services.length}</p>
            </div>
          )}
        </div>

        <div className="mb-6">
          <h3 className="font-serif text-xl font-semibold mb-3">{t('school.description')}</h3>
          <p className="text-secondary whitespace-pre-line">{getLocalizedText(school.description, i18n.language)}</p>
        </div>

        {school.website && (
          <div className="mb-6">
            <a href={school.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-gold hover:underline" style={{ color: '#D4A574' }}>
              <Globe className="h-4 w-4 mr-2" /> {school.website}
            </a>
          </div>
        )}

        {/* Courses Section */}
        {courses.length > 0 && (
          <div className="mb-8">
            <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-500" />
              {t('profile.myCourses')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} language={i18n.language} />
              ))}
            </div>
          </div>
        )}

        {/* Services Section - only shown if user has teacher profile */}
        {services.length > 0 && (
          <div>
            <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
              <Heart className="h-5 w-5 text-purple-500" />
              {t('profile.myServices')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => (
                <ServiceCard key={service.id} service={service} language={i18n.language} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
